import type { SoundEvent } from './soundEvents';

const MUTE_STORAGE_KEY = 'pm_muted';
const MASTER_GAIN = 0.2; // moderate -- audible but never harsh

/** Safari (and older WebKit) exposes the constructor as `webkitAudioContext`
 * instead of the standard `AudioContext`. `lib.dom.d.ts` doesn't declare
 * that vendor-prefixed name, so it's typed explicitly here (narrow, no
 * `any`) instead of casting the whole `window` to `any`. Extends
 * `typeof window` (not just `Window`) so the standard `AudioContext` global
 * -- itself declared as a top-level `declare var`, reachable via `window`
 * only through the `typeof globalThis` half of `window`'s real type -- stays
 * visible on this narrowed type too. */
type WindowWithWebkitAudio = typeof window & {
  webkitAudioContext?: typeof AudioContext;
};

function readStoredMuted(): boolean {
  try {
    return window.localStorage.getItem(MUTE_STORAGE_KEY) === '1';
  } catch {
    return false; // storage disabled/unavailable -- default unmuted
  }
}

function writeStoredMuted(muted: boolean): void {
  try {
    window.localStorage.setItem(MUTE_STORAGE_KEY, muted ? '1' : '0');
  } catch {
    // best-effort persistence only; never let this throw into game logic
  }
}

/** Small oscillator+gain synth wrapping the Web Audio API. Every method is a
 * safe no-op when Web Audio isn't available (jsdom in unit tests has no
 * `AudioContext`, and some embedded/older browsers lack it too) -- this
 * class must NEVER throw into the game loop that calls `play()`. */
export class SoundPlayer {
  private ctx: AudioContext | null = null;
  private ctxInitAttempted = false;
  private _muted: boolean;
  private chompToggle = false;
  private listeners = new Set<(muted: boolean) => void>();

  constructor() {
    this._muted = readStoredMuted();
  }

  get muted(): boolean {
    return this._muted;
  }

  setMuted(muted: boolean): void {
    this._muted = muted;
    writeStoredMuted(muted);
    for (const l of this.listeners) l(muted);
  }

  /** Lets any component (MuteButton, SettingsModal, ...) react to a mute
   * change made through a DIFFERENT component -- both are otherwise just
   * plain `useState` mirrors of this singleton and would silently drift
   * out of sync with each other without this (the settings brief requires
   * "both reflect sound.muted"). Returns an unsubscribe fn. */
  subscribe(listener: (muted: boolean) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Lazily creates the AudioContext on first real use, and resumes it if a
   * browser has it 'suspended' pending a user gesture. Call this from a
   * click/tap handler that already happens before gameplay (SPEC: the
   * Play/Start-round path) -- browsers refuse to let a bare page start
   * audio without one. */
  resume(): void {
    try {
      const ctx = this.ensureContext();
      if (!ctx) return;
      if (ctx.state === 'suspended') {
        void ctx.resume();
      }
    } catch {
      // never let audio setup errors bubble into the game
    }
  }

  play(event: SoundEvent): void {
    if (this._muted) return;
    try {
      const ctx = this.ensureContext();
      if (!ctx) return;
      if (ctx.state === 'suspended') {
        // best-effort; if this is the very first user gesture we may not
        // have resumed yet -- try here too, but never await/block on it.
        void ctx.resume();
      }
      this.synth(ctx, event);
    } catch {
      // audio must never crash gameplay
    }
  }

  private ensureContext(): AudioContext | null {
    if (this.ctx) return this.ctx;
    if (this.ctxInitAttempted) return null; // already tried and failed
    this.ctxInitAttempted = true;
    const w = window as WindowWithWebkitAudio;
    const Ctx = w.AudioContext ?? w.webkitAudioContext;
    if (!Ctx) return null; // no Web Audio support (e.g. jsdom) -- stay a no-op
    this.ctx = new Ctx();
    return this.ctx;
  }

  private synth(ctx: AudioContext, event: SoundEvent): void {
    const now = ctx.currentTime;
    switch (event) {
      case 'chomp': {
        // Classic "waka" blip -- alternates pitch each call, short + cheap
        // since this fires often.
        this.chompToggle = !this.chompToggle;
        this.tone(ctx, now, {
          type: 'square',
          freq: this.chompToggle ? 400 : 300,
          duration: 0.07,
          gain: 0.5,
        });
        break;
      }
      case 'energizer': {
        // Warble/pulse: a short frequency wobble, clearly distinct from chomp.
        this.sweep(ctx, now, { type: 'sawtooth', from: 220, to: 440, duration: 0.22, gain: 0.55 });
        this.sweep(ctx, now + 0.08, { type: 'sawtooth', from: 440, to: 220, duration: 0.14, gain: 0.4 });
        break;
      }
      case 'ghostEaten': {
        // Rising pitch sweep -- reward feel.
        this.sweep(ctx, now, { type: 'square', from: 200, to: 900, duration: 0.2, gain: 0.6 });
        break;
      }
      case 'fruit': {
        // Short 2-3 note pleasant jingle.
        this.tone(ctx, now, { type: 'triangle', freq: 523.25, duration: 0.08, gain: 0.5 });
        this.tone(ctx, now + 0.08, { type: 'triangle', freq: 659.25, duration: 0.08, gain: 0.5 });
        this.tone(ctx, now + 0.16, { type: 'triangle', freq: 783.99, duration: 0.12, gain: 0.55 });
        break;
      }
      case 'death': {
        // Descending pitch sweep -- classic Pac-Man death feel.
        this.sweep(ctx, now, { type: 'sawtooth', from: 700, to: 80, duration: 0.25, gain: 0.6 });
        break;
      }
      case 'extraLife': {
        // Short bright up-arpeggio -- rare, so a touch louder/longer.
        this.tone(ctx, now, { type: 'triangle', freq: 523.25, duration: 0.07, gain: 0.55 });
        this.tone(ctx, now + 0.07, { type: 'triangle', freq: 659.25, duration: 0.07, gain: 0.55 });
        this.tone(ctx, now + 0.14, { type: 'triangle', freq: 987.77, duration: 0.15, gain: 0.6 });
        break;
      }
      case 'levelUp': {
        // Short 2-3 note fanfare.
        this.tone(ctx, now, { type: 'square', freq: 392.0, duration: 0.09, gain: 0.5 });
        this.tone(ctx, now + 0.09, { type: 'square', freq: 523.25, duration: 0.09, gain: 0.5 });
        this.tone(ctx, now + 0.18, { type: 'square', freq: 659.25, duration: 0.16, gain: 0.55 });
        break;
      }
    }
  }

  /** One oscillator with a short attack/decay envelope -- keeps every sound
   * comfortably under ~250ms so rapid chomps never pile up. */
  private tone(
    ctx: AudioContext,
    startAt: number,
    opts: { type: OscillatorType; freq: number; duration: number; gain: number },
  ): void {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = opts.type;
    osc.frequency.setValueAtTime(opts.freq, startAt);

    const peak = MASTER_GAIN * opts.gain;
    const attack = Math.min(0.012, opts.duration * 0.25);
    gain.gain.setValueAtTime(0, startAt);
    gain.gain.linearRampToValueAtTime(peak, startAt + attack);
    gain.gain.exponentialRampToValueAtTime(0.0001, startAt + opts.duration);

    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(startAt);
    osc.stop(startAt + opts.duration + 0.02);
  }

  /** One oscillator whose frequency glides from `from` to `to` over
   * `duration` seconds, with the same short attack/decay envelope as
   * `tone()`. */
  private sweep(
    ctx: AudioContext,
    startAt: number,
    opts: { type: OscillatorType; from: number; to: number; duration: number; gain: number },
  ): void {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = opts.type;
    osc.frequency.setValueAtTime(opts.from, startAt);
    osc.frequency.exponentialRampToValueAtTime(Math.max(1, opts.to), startAt + opts.duration);

    const peak = MASTER_GAIN * opts.gain;
    const attack = Math.min(0.012, opts.duration * 0.2);
    gain.gain.setValueAtTime(0, startAt);
    gain.gain.linearRampToValueAtTime(peak, startAt + attack);
    gain.gain.exponentialRampToValueAtTime(0.0001, startAt + opts.duration);

    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(startAt);
    osc.stop(startAt + opts.duration + 0.02);
  }
}

/** Module-singleton -- one AudioContext for the whole app, matching how a
 * real browser only ever wants one. */
export const sound = new SoundPlayer();
